/**
 * @element qds-tooltip
 * Noninteractive text help displayed on hover or keyboard focus.
 * @attr {string} for - Anchor ID in the same root or owner document.
 * @attr {string} text - Tooltip text; default slotted text is the fallback.
 * @attr {number} show-delay - Hover delay in milliseconds, default 400.
 * @attr {number} hide-delay - Pointer leave grace period, default 100.
 * @attr {'top'|'bottom'|'left'|'right'} placement - Preferred position, default top.
 * @attr {boolean} disabled - Suppress both the tooltip and its accessible description.
 * @property {HTMLElement|null} anchor - Explicit anchor, overriding for/slot.
 * @property {boolean} open - Read-only visible state.
 * @slot anchor - Trigger element when no anchor property or for attribute is supplied.
 * @slot - Plain-text fallback content when text is absent; markup is not rendered interactively.
 * @fires toggle - Actual visibility changes; detail {open}.
 * @csspart tooltip - Noninteractive tooltip surface.
 */
import { loadBaseCSS } from '../shared/button-utils.js';
import { AnchoredOverlayController, overlayFocusable } from '../shared/overlay-utils.js';
import type { OverlayPlacement } from '../shared/overlay-utils.js';

let tooltipId = 0;
export class QdsTooltip extends HTMLElement {
  static get observedAttributes(): string[] { return ['for', 'text', 'disabled', 'placement', 'show-delay', 'hide-delay']; }
  private _anchor: HTMLElement | null = null;
  private _boundAnchor: HTMLElement | null = null;
  private _description: HTMLSpanElement | null = null;
  private _descriptionTarget: HTMLElement | null = null;
  private _panel: HTMLElement;
  private _controller: AnchoredOverlayController;
  private _observer: MutationObserver;
  private _timer: ReturnType<typeof setTimeout> | null = null;
  private _hover = false;
  private _focus = false;
  private _dismissed = false;
  constructor() {
    super(); this.attachShadow({ mode: 'open' });
    this.shadowRoot!.innerHTML = `<style>
      :host { display: inline-block; }
      .tooltip { position: fixed; inset: auto; margin: 0; box-sizing: border-box; width: max-content; max-width: min(24rem, calc(100vw - 1rem)); padding: .5rem .75rem; border: 1px solid var(--theme-border-color, #e0e0e0); border-radius: 4px; background: var(--theme-background-primary, #fff); color: var(--theme-text-primary, #333); font: .875rem/1.4 var(--font-family-primary, 'IBM Plex Sans', sans-serif); box-shadow: var(--shadow-light, 0 1px 3px rgba(0,0,0,.1)); overflow-wrap: anywhere; z-index: var(--qds-tooltip-z, 1100); }
      [hidden] { display: none !important; }
    </style><slot name="anchor"></slot><slot id="source" hidden></slot><div class="tooltip" part="tooltip" role="tooltip" hidden></div>`;
    this._panel = this.shadowRoot!.querySelector('.tooltip')!;
    this._controller = new AnchoredOverlayController(this, this._panel, {
      anchor: () => this.anchor,
      placement: () => ['top', 'bottom', 'left', 'right'].includes(this.getAttribute('placement') || '') ? this.getAttribute('placement') as OverlayPlacement : 'top',
      outside: () => false,
      outsidePassthrough: true,
      onDismiss: () => { this._dismissed = true; this.hide(); },
    });
    this.shadowRoot!.querySelector('slot[name="anchor"]')!.addEventListener('slotchange', () => this._bindAnchor());
    this.shadowRoot!.querySelector('#source')!.addEventListener('slotchange', () => this._syncText());
    this._panel.addEventListener('pointerenter', this._enter);
    this._panel.addEventListener('pointerleave', this._leave);
    this._observer = new MutationObserver(() => this._syncText());
  }
  get open(): boolean { return this._controller.open; }
  get anchor(): HTMLElement | null {
    if (this._anchor) return this._anchor;
    const id = this.getAttribute('for');
    if (id) {
      const root = this.getRootNode() as Document | ShadowRoot;
      return (root.getElementById?.(id) || this.ownerDocument.getElementById(id)) as HTMLElement | null;
    }
    return this.shadowRoot!.querySelector<HTMLSlotElement>('slot[name="anchor"]')!.assignedElements()[0] as HTMLElement || null;
  }
  set anchor(value: HTMLElement | null) { this._anchor = value; if (this.isConnected) this._bindAnchor(); }
  connectedCallback(): void {
    loadBaseCSS();
    if (Object.prototype.hasOwnProperty.call(this, 'anchor')) { const value = this.anchor; delete (this as any).anchor; this.anchor = value; }
    this._bindAnchor(); this._observer.observe(this, { childList: true, characterData: true, subtree: true });
    queueMicrotask(() => { if (this.isConnected) this._bindAnchor(); });
  }
  disconnectedCallback(): void { this.hide(); this._observer.disconnect(); this._unbindAnchor(); }
  attributeChangedCallback(name: string): void {
    if (!this._panel) return;
    if (name === 'for' && this.isConnected) this._bindAnchor();
    if (this.hasAttribute('disabled')) { this.hide(); this._removeDescription(); }
    else this._syncText();
    this._controller.reposition();
  }
  /** Immediately shows text without moving focus. */
  show(): void {
    this._clearTimer();
    if (this.hasAttribute('disabled') || !this._panel.textContent || this._dismissed) return;
    const wasOpen = this.open;
    if (this._controller.show() && !wasOpen) this.dispatchEvent(new CustomEvent('toggle', { detail: { open: true }, bubbles: true, composed: true }));
  }
  hide(): void {
    this._clearTimer(); const wasOpen = this.open; this._controller.hide();
    if (wasOpen) this.dispatchEvent(new CustomEvent('toggle', { detail: { open: false }, bubbles: true, composed: true }));
  }
  private _clearTimer(): void { if (this._timer !== null) clearTimeout(this._timer); this._timer = null; }
  private _delay(name: string, fallback: number): number {
    const value = Number(this.getAttribute(name) ?? fallback); return Number.isFinite(value) ? Math.max(0, value) : fallback;
  }
  private _enter = (): void => {
    this._hover = true; this._clearTimer();
    if (!this._dismissed) this._timer = setTimeout(() => this.show(), this._delay('show-delay', 400));
  };
  private _leave = (): void => {
    this._hover = false; this._clearTimer();
    if (!this._focus) this._timer = setTimeout(() => { this.hide(); this._dismissed = false; }, this._delay('hide-delay', 100));
  };
  private _focusIn = (): void => { this._focus = true; this._dismissed = false; this._syncText(); this.show(); };
  private _focusOut = (): void => {
    this._focus = false;
    if (!this._hover) { this.hide(); this._dismissed = false; }
  };
  private _unbindAnchor(): void {
    if (this._boundAnchor) {
      this._boundAnchor.removeEventListener('pointerenter', this._enter); this._boundAnchor.removeEventListener('pointerleave', this._leave);
      this._boundAnchor.removeEventListener('focusin', this._focusIn); this._boundAnchor.removeEventListener('focusout', this._focusOut);
    }
    this._removeDescription(); this._boundAnchor = null;
  }
  private _removeDescription(): void {
    if (!this._description) return;
    const tokens = (this._descriptionTarget?.getAttribute('aria-describedby') || '').split(/\s+/).filter(token => token && token !== this._description!.id);
    if (tokens.length) this._descriptionTarget?.setAttribute('aria-describedby', tokens.join(' ')); else this._descriptionTarget?.removeAttribute('aria-describedby');
    this._description.remove(); this._description = null; this._descriptionTarget = null;
  }
  private _bindAnchor(): void {
    const anchor = this.anchor;
    if (anchor !== this._boundAnchor) {
      this.hide(); this._unbindAnchor(); this._boundAnchor = anchor; this._hover = false; this._focus = false; this._dismissed = false;
      anchor?.addEventListener('pointerenter', this._enter); anchor?.addEventListener('pointerleave', this._leave);
      anchor?.addEventListener('focusin', this._focusIn); anchor?.addEventListener('focusout', this._focusOut);
      if (anchor?.localName.includes('-') && !customElements.get(anchor.localName)) {
        customElements.whenDefined(anchor.localName).then(() => { if (this._boundAnchor === anchor) this._syncText(); });
      }
    }
    this._syncText();
  }
  private _syncText(): void {
    const source = this.shadowRoot!.querySelector<HTMLSlotElement>('#source')!;
    const text = this.getAttribute('text') ?? source.assignedNodes().map(node => node.textContent || '').join(' ').trim();
    if (this._panel.textContent !== text) this._panel.textContent = text;
    if (!text || this.hasAttribute('disabled')) { this.hide(); this._removeDescription(); return; }
    // A custom button's semantic/focus target may be inside its open shadow root.
    const target = this._boundAnchor?.shadowRoot && this._boundAnchor.tabIndex < 0
      ? overlayFocusable(this._boundAnchor)[0] || this._boundAnchor : this._boundAnchor;
    if (target !== this._descriptionTarget) this._removeDescription();
    if (target && !this._description) {
      this._descriptionTarget = target;
      this._description = this.ownerDocument.createElement('span');
      this._description.id = `qds-tooltip-description-${++tooltipId}`; this._description.hidden = true;
      this._description.slot = '__qds-description';
      // ID references cannot cross a shadow boundary. Place the hidden textual
      // description beside its anchor in the same root, never in document.body.
      target.after(this._description);
      const describedBy = target.getAttribute('aria-describedby');
      target.setAttribute('aria-describedby', [describedBy, this._description.id].filter(Boolean).join(' '));
    }
    if (this._description && this._description.textContent !== text) this._description.textContent = text;
    this._controller.reposition();
  }
}
if (!customElements.get('qds-tooltip')) customElements.define('qds-tooltip', QdsTooltip);
export default QdsTooltip;
